import { NodeModel } from '@diagram/model/node.model';

type NodeMap = { [key: string]: NodeModel };

const getBounds = (node: NodeModel) => {
  const width = node.width;
  const height = node.height;
  return {
    left: node.position.x,
    right: node.position.x + width,
    top: node.position.y,
    bottom: node.position.y + height,
    centerX: node.position.x + width / 2,
    centerY: node.position.y + height / 2,
    width,
    height,
  };
};

export const shiftNodesForSpacing = (
  nodes: NodeMap,
  insertedNodeId: string
) => {
  const minGap = 150;
  const threshold = 30;
  const maxIterations = 1000;
  let iterationCount = 0;

  const clone = (node: NodeModel): NodeModel => ({
    ...node,
  });

  const newNodes = Object.fromEntries(
    Object.entries(nodes).map(([id, node]) => [id, clone(node)])
  );

  // Save original centers for alignment delta logic
  const originalCenters = Object.fromEntries(
    Object.entries(newNodes).map(([id, node]) => [
      id,
      {
        centerX: getBounds(node).centerX,
        centerY: getBounds(node).centerY,
      },
    ])
  );

  const queue = new Set<string>([insertedNodeId]);

  const shiftGroupOnX = (movedId: string, deltaX: number) => {
    const movedCenterX = originalCenters[movedId].centerX;
    for (const [id, node] of Object.entries(newNodes)) {
      if (id === movedId) continue;
      if (Math.abs(originalCenters[id].centerX - movedCenterX) < threshold) {
        node.position.x += deltaX;
        queue.add(id);
      }
    }
  };

  const shiftGroupOnY = (movedId: string, deltaY: number) => {
    const movedCenterY = originalCenters[movedId].centerY;
    for (const [id, node] of Object.entries(newNodes)) {
      if (id === movedId) continue;
      if (Math.abs(originalCenters[id].centerY - movedCenterY) < threshold) {
        node.position.y += deltaY;
        queue.add(id);
      }
    }
  };

  const maxDuration = 5000; // ms — tweak this
  const start = performance.now();

  while (queue.size > 0) {
    if (++iterationCount > maxIterations) {
      return null;
    }
    if (performance.now() - start > maxDuration) {
      console.warn('Auto-spacing timed out — breaking early');
      return null;
    }

    const idsToProcess = Array.from(queue);
    queue.clear();

    for (const id of idsToProcess) {
      const current = newNodes[id];
      const currentBounds = getBounds(current);

      // HORIZONTAL SWEEP
      const horizontal = Object.entries(newNodes)
        .filter(
          ([otherId, other]) =>
            otherId !== id &&
            Math.abs(getBounds(other).centerY - currentBounds.centerY) <
              threshold
        )
        .map(([otherId, other]) => ({ id: otherId, node: other }))
        .concat({ id, node: current })
        .sort((a, b) => getBounds(a.node).centerX - getBounds(b.node).centerX);

      const insertIndex = horizontal.findIndex((n) => n.id === id);

      for (let i = insertIndex + 1; i < horizontal.length; i++) {
        const prevBounds = getBounds(horizontal[i - 1].node);
        const { id: otherId, node: other } = horizontal[i];
        const otherBounds = getBounds(other);

        const overlap = prevBounds.right + minGap > otherBounds.left;
        if (overlap) {
          const shift = prevBounds.right + minGap - otherBounds.left;
          if (shift > 0.5) {
            other.position.x += shift;
            queue.add(otherId);
            shiftGroupOnX(otherId, shift);
          }
        }
      }

      for (let i = insertIndex - 1; i >= 0; i--) {
        const nextBounds = getBounds(horizontal[i + 1].node);
        const { id: otherId, node: other } = horizontal[i];
        const otherBounds = getBounds(other);

        const overlap = otherBounds.right + minGap > nextBounds.left;
        if (overlap) {
          const shift = otherBounds.right + minGap - nextBounds.left;
          if (shift > 0.5) {
            other.position.x -= shift;
            queue.add(otherId);
            shiftGroupOnX(otherId, -shift);
          }
        }
      }

      // VERTICAL SWEEP
      const vertical = Object.entries(newNodes)
        .filter(
          ([otherId, other]) =>
            otherId !== id &&
            Math.abs(getBounds(other).centerX - currentBounds.centerX) <
              threshold
        )
        .map(([otherId, other]) => ({ id: otherId, node: other }))
        .concat({ id, node: current })
        .sort((a, b) => getBounds(a.node).centerY - getBounds(b.node).centerY);

      const insertYIndex = vertical.findIndex((n) => n.id === id);

      for (let i = insertYIndex + 1; i < vertical.length; i++) {
        const prevBounds = getBounds(vertical[i - 1].node);
        const { id: otherId, node: other } = vertical[i];
        const otherBounds = getBounds(other);

        const overlap = prevBounds.bottom + minGap > otherBounds.top;
        if (overlap) {
          const shift = prevBounds.bottom + minGap - otherBounds.top;
          if (shift > 0.5) {
            other.position.y += shift;
            queue.add(otherId);
            shiftGroupOnY(otherId, shift);
          }
        }
      }

      for (let i = insertYIndex - 1; i >= 0; i--) {
        const nextBounds = getBounds(vertical[i + 1].node);
        const { id: otherId, node: other } = vertical[i];
        const otherBounds = getBounds(other);

        const overlap = otherBounds.bottom + minGap > nextBounds.top;
        if (overlap) {
          const shift = otherBounds.bottom + minGap - nextBounds.top;
          if (shift > 0.5) {
            other.position.y -= shift;
            queue.add(otherId);
            shiftGroupOnY(otherId, -shift);
          }
        }
      }
    }
  }

  return Object.values(newNodes);
};

export default shiftNodesForSpacing;
